<template>
  <div class="bg-white -ml-6 -mr-6">
    <div class="grid gap-8 lg:grid-cols-12 p-8">
      <!-- Tenant Summary -->
      <div class="lg:col-span-5 xl:col-span-4">
        <div class="flex items-start space-x-4">
          <img
            v-if="tenant.logo"
            :src="tenant.logo"
            alt="Tenant Logo"
            class="rounded-lg object-cover border border-gray-200"
            style="object-fit: contain; width: 96px; height: 96px;"
          />
          <span v-else>
            <Icon name="EmptyLogo" />
          </span>
          <div class="flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <h2
                v-if="tenant.name"
                class="text-xl font-bold text-slate-800 leading-tight"
              >
                {{ tenant.name || "-" }}
              </h2>
              <div
                class="border ml-2 mt-4 md:mt-0 flex flex-shrink-0 text-sm border-gray-200 rounded"
              >
                <div
                  class="px-2 py-1 relative rounded flex items-center gap-2"
                  :class="tenantStatusWrapperClass"
                >
                  <span :class="statusDotClass(tenantStatusLabel)"></span>
                  <span
                    class="status rounded text-sm font-medium"
                    :class="tenantStatusClass"
                  >
                    {{ tenantStatusLabel || "-" }}
                  </span>
                </div>
              </div>
            </div>
            <a
              v-if="tenant.website"
              :href="tenant.website"
              class="text-sm text-slate-800 hover:text-slate-900 break-all"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ tenant.website || "-" }}
            </a>

            <div class="mt-4 space-y-1">
              <InfoRow>
                <template #icon>
                  <Icon name="OwnerIcon" />
                </template>
                <p class="text-slate-800 font-medium" v-if="primaryOwner">
                  {{ primaryOwner.name }}
                  <span :class="roleBadge.class" :style="roleBadge.style">
                    {{ primaryOwner.role }}
                  </span>
                </p>
                <p
                  v-else-if="tenant.contact.name"
                  class="text-slate-800 font-medium"
                >
                  {{ tenant.contact.name || "-" }}
                  <span :class="roleBadge.class" :style="roleBadge.style">
                    {{ tenant.contact.role || "-" }}
                  </span>
                </p>
              </InfoRow>

              <InfoRow>
                <template #icon>
                  <Icon name="EmailIcon" />
                </template>
                <p>{{ tenant.contact.email || "-" }}</p>
              </InfoRow>

              <InfoRow>
                <template #icon>
                  <Icon name="CountryIcon" />
                </template>
                <p>
                  {{ tenant.contact.country || "-" }},
                  {{ tenant.contact.city || "-" }},
                  {{ tenant.contact.state || "-" }}
                </p>
              </InfoRow>

              <InfoRow>
                <template #icon>
                  <Icon name="PhoneIcon" />
                </template>
                <p>{{ tenant.contact.phone || "-" }}</p>
              </InfoRow>

              <InfoRow>
                <template #icon>
                  <Icon name="CalendarIcon" />
                </template>
                <p>{{ tenant.registeredOn || "-" }}</p>
              </InfoRow>
            </div>
          </div>
        </div>
      </div>

      <!-- Subscription Info -->
      <div class="lg:col-span-2 xl:col-span-3">
        <div class="space-y-6">
          <div
            class="cursor-pointer transition hover:text-blue-600"
            @click="openSubscriptionDrawer"
          >
            <p class="text-sm font-medium tracking-wide text-slate-800">
              Subscription
            </p>
            <p class="text-base font-bold text-slate-800 mt-1">
              {{ subscription.planName || "-" }}
            </p>
            <p class="text-xs text-slate-800 mt-0.5">
              {{ subscription.startedOn || "-" }}
            </p>
          </div>

          <div>
            <p class="text-sm font-medium tracking-wide text-slate-800">
              Next Billing
            </p>
            <p class="text-base font-bold text-slate-800 mt-1">
              {{ subscription.currency || "-" }}
              {{ subscription.nextAmount || "-" }}
            </p>
            <p class="text-xs text-slate-800 mt-0.5">
              {{ subscription.nextBillingDate || "-" }}
            </p>
          </div>
        </div>
      </div>
      <!-- {{ metrics }} -->
      <!-- Usage Metrics -->
      <div class="lg:col-span-5 xl:col-span-5">
        <div class="grid gap-6 sm:grid-cols-2">
          <button
            v-for="metric in metrics"
            :key="metric.name"
            type="button"
            class="flex w-full items-center gap-4 rounded-lg border border-transparent bg-white p-2 text-left transition hover:border-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            @click="openMetricDrawer(metric)"
          >
            <CircleProgress
              :value="metric.value"
              :total="metric.total"
              :color="metric.color"
              :trail-color="metric.trail"
              size="sm"
              :show-percent="false"
              :inner-padding="6"
            >
              <span class="sr-only">{{ metric.name || "-" }}</span>
            </CircleProgress>
            <div class="flex flex-col">
              <p class="text-xs font-medium text-slate-500">
                {{ metric.name || "-" }}
              </p>
              <div class="mt-0.5 flex items-baseline gap-2 whitespace-nowrap">
                <span class="text-lg font-semibold text-slate-800">
                  {{ metric.value || "0" }}
                </span>
                <span class="text-sm text-slate-500 whitespace-nowrap">
                  {{ metric.secondary || "0" }}
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
    <Drawer
      v-if="isMetricDrawerOpen"
      :is-drawer-open="isMetricDrawerOpen"
      title=""
      :is-right-drawer="true"
      :drawer-width="drawerWidth"
      drawer-padding="p-0"
      @close-drawer="closeMetricDrawer"
    >
      <template #main-content>
        <template v-if="selectedMetric">
          <UserEmailDrawerContent
            v-if="
              ['Users', 'Email Outbox', 'Email Inbox'].includes(
                selectedMetric.name
              )
            "
            :metric="selectedMetric"
            :initial-potential-users="potentialUsers"
            @close="closeMetricDrawer"
          />
          <StorageDrawerContent
            v-else
            :metric="selectedMetric"
            @close="closeMetricDrawer"
          />
        </template>
      </template>
    </Drawer>
    <Drawer
      v-if="isSubscriptionDrawerOpen"
      :is-drawer-open="isSubscriptionDrawerOpen"
      title=""
      :is-right-drawer="true"
      drawer-width="w-full md:w-2/5"
      drawer-padding="p-0"
      @close-drawer="closeSubscriptionDrawer"
    >
      <template #main-content>
        <SubscriptionDrawerContent
          :title="subscriptionDrawerTitle"
          :plan="subscriptionPlanSummary"
          :status="subscriptionStatusDisplay"
          :next-billing="subscriptionNextBillingDisplay"
          :logs="subscriptionLogs"
          @close="closeSubscriptionDrawer"
        />
      </template>
    </Drawer>
    <Tabs
      :tabs="tabs"
      v-model="activeTab"
      :component-props="{ tenantId: $route.params.id }"
    />
  </div>
</template>

<script>
import Tabs from "../components/core/Tabs.vue";
import CircleProgress from "../components/core/CircleProgress.vue";
import Icon from "../components/core/Icon.vue";
import InfoRow from "../components/core/InfoRow.vue";
import Drawer from "../components/core/Drawer.vue";
import UserEmailDrawerContent from "../components/Forms/UserEmailDrawerContent.vue";
import StorageDrawerContent from "../components/Forms/StorageDrawerContent.vue";
import SubscriptionDrawerContent from "../components/Forms/SubscriptionDrawerContent.vue";
import OverviewTab from "../components/tabs/OverviewTab.vue";
import UsersTab from "../components/tabs/UsersTab.vue";
import OfficeTab from "../components/tabs/OfficeTab.vue";
import SubscriptionTab from "../components/tabs/SubscriptionTab.vue";
import IntegrationTab from "../components/tabs/IntegrationTab.vue";
import SmsTab from "../components/tabs/SmsTab.vue";
import AdvanceSettingTab from "../components/tabs/AdvanceSettingTab.vue";
import {
  getStatusClass,
  getStatusClassWrapper,
  getStatusLabel
} from "../utils/statusHelpers";
export default {
  name: "AnalyticDashboard",
  components: {
    Tabs,
    CircleProgress,
    Icon,
    InfoRow,
    Drawer,
    UserEmailDrawerContent,
    StorageDrawerContent,
    SubscriptionDrawerContent
  },
  data() {
    return {
      apiData: null,
      activeTab: "overview",
      tabs: [
        { label: "Overview", value: "overview", component: OverviewTab },
        { label: "Users", value: "users", component: UsersTab },
        { label: "Offices", value: "offices", component: OfficeTab },
        {
          label: "Subscription",
          value: "subscription",
          component: SubscriptionTab
        },
        {
          label: "Integration",
          value: "integration",
          component: IntegrationTab
        },
        { label: "SMS", value: "sms", component: SmsTab },
        {
          label: "Advance Setting",
          value: "advance-setting",
          component: AdvanceSettingTab
        }
      ],
      tenant: {
        name: "",
        status: "",
        logo: null,
        website: "",
        registeredOn: "",
        contact: {
          name: "",
          role: "",
          email: "",
          country: "",
          city: "",
          state: "",
          phone: ""
        }
      },
      subscription: {
        planName: "No active subscription",
        startedOn: "",
        currency: "",
        nextAmount: "",
        nextBillingDate: "",
        status: ""
      },
      metrics: [],
      isMetricDrawerOpen: false,
      selectedMetric: null,
      drawerWidth: "w-full md:w-2/5",
      potentialUsers: null,
      isSubscriptionDrawerOpen: false,
      subscriptionDetail: {
        planName: "premium-npr-annualy",
        status: "Active",
        subscribedOn: "2022-08-21 14:00:00",
        billingCycle: "Annual",
        trialEndsAt: "2025-01-24 13:59:59",
        nextBillingDate: "2025-01-24 13:59:59",
        nextBillingAmount: "NPR 1000",
        createdBy: "Ramu"
      },
      subscriptionLogs: []
    };
  },
  computed: {
    tenantBasicInfo() {
      return this.$store.getters["TenantAnalyticsStore/tenantBasicInfo"];
    },
    tenantStatusWrapperClass() {
      return getStatusClassWrapper({
        ...this.tenant,
        status: this.subscription.status || this.tenant.status
      });
    },
    tenantStatusClass() {
      return getStatusClass({
        ...this.tenant,
        status: this.subscription.status || this.tenant.status
      });
    },
    tenantStatusLabel() {
      const statusSource = this.subscription.status || this.tenant.status;
      return getStatusLabel(statusSource);
    },
    roleBadge() {
      return {
        class:
          "inline-flex items-center gap-2 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none",
        style: {
          backgroundColor: "#F0F9FF",
          color: "#0284C7",
          borderRadius: "4px",
          padding: "4px",
          marginRight: "4px"
        }
      };
    },
    primaryOwner() {
      const owner = this.$store.getters[
        "TenantAnalyticsStore/tenantAdminOwner"
      ];
      if (!owner) {
        return null;
      }

      const nameParts = [owner.first_name, owner.last_name].filter(Boolean);
      return {
        name: nameParts.length ? nameParts.join(" ") : owner.email,
        role: owner.role_name || owner.job_title || "Owner",
        email: owner.email || "",
        phone: owner.phone || ""
      };
    },
    subscriptionDrawerTitle() {
      return "Subscription Detail";
    },
    subscriptionPlanSummary() {
      return {
        name: this.subscriptionDetail.planName,
        billingCycle:
          this.subscriptionDetail.billingCycle ||
          this.subscriptionDetail.subscribedOn
      };
    },
    subscriptionStatusDisplay() {
      const statusKey = (this.subscriptionDetail.status || "").toString().toLowerCase();
      const statusMap = {
        active: {
          label: "Active",
          background: "bg-green-100",
          text: "text-green-600",
          dot: "bg-green-500"
        },
        in_trial: {
          label: "In Trial",
          background: "bg-blue-100",
          text: "text-blue-600",
          dot: "bg-blue-500"
        },
        non_renewing: {
          label: "Non Renewing",
          background: "bg-orange-100",
          text: "text-orange-600",
          dot: "bg-orange-500"
        },
        cancelled: {
          label: "Cancelled",
          background: "bg-red-100",
          text: "text-red-600",
          dot: "bg-red-500"
        }
      };
      const fallback = {
        label: this.subscriptionDetail.status || "Inactive",
        background: "bg-slate-100",
        text: "text-slate-600",
        dot: "bg-slate-400"
      };
      return statusMap[statusKey] || fallback;
    },
    subscriptionNextBillingDisplay() {
      return {
        label: "Next Billing Date",
        date:
          this.subscriptionDetail.nextBillingDate ||
          this.subscriptionDetail.trialEndsAt,
        amount: this.subscriptionDetail.nextBillingAmount
      };
    }
  },
  watch: {
    tenantBasicInfo: {
      immediate: true,
      handler(newInfo) {
        const payload = newInfo?.data ?? newInfo ?? null;
        if (!payload) {
          this.apiData = null;
          this.resetSummaryData();
          return;
        }

        this.apiData = payload;
        this.resetSummaryData();
        this.mapSummaryData(payload);
      }
    }
  },
  methods: {
    async fetchTenantData() {
      this.$loading(true);
      try {
        const tenantId = this.$route.params.id;

        if (!tenantId) {
          this.$toasted.error("Tenant ID is missing");
          this.$loading(false);
          return;
        }

        await this.$store.dispatch(
          "TenantAnalyticsStore/fetchTenantBasicInfo",
          tenantId
        );
      } catch (error) {
        this.$toasted.error("Failed to fetch tenant data");
        this.apiData = null;
      } finally {
        this.$loading(false);
      }
    },
    resetSummaryData() {
      this.tenant = {
        name: "",
        status: "",
        logo: null,
        website: "",
        registeredOn: "",
        contact: {
          name: "",
          role: "",
          email: "",
          country: "",
          city: "",
          state: "",
          phone: ""
        }
      };

      this.subscription = {
        planName: "No active subscription",
        startedOn: "",
        currency: "",
        nextAmount: "",
        nextBillingDate: "",
        status: ""
      };

      this.metrics = [];
      this.potentialUsers = null;
      this.subscriptionLogs = [];
    },
    mapSummaryData(apiData) {
      if (!apiData) {
        return;
      }

      const company = apiData.tenantCompanyDetails || {};

      const normalizedStatus = this.normalizeStatus(
        apiData.tenantSubscriptionStatus
      );
      const logoUrl = this.normalizeLogoUrl(
        company.logo || apiData.logo || null
      );
      const websiteUrl = this.normalizeWebsite(
        company.company_website || apiData.domainUrl || null
      );
      const registeredOn = apiData.tenantJoinedAt
        ? this.formatDateTime(apiData.tenantJoinedAt)
        : "";

      this.tenant = {
        name: company.name || this.tenant.name,
        status: normalizedStatus,
        logo: logoUrl,
        website: websiteUrl,
        registeredOn,
        contact: {
          name: company.name || "",
          role: company.contact_role || "",
          email: company.company_email || "",
          country: company.country_name || "",
          city: company.city || "",
          state: company.state || "",
          phone: company.company_phone || ""
        }
      };

      const subscription = apiData.subscription || {};
      const nextAmount =
        subscription.next_billing_amount ?? subscription.next_billing_amount;
      const nextBillingDate =
        subscription.next_billing_at ?? subscription.next_billing_at;

      this.subscription = {
        planName:
          subscription.plan_name ||
          subscription.planName ||
          "No active subscription",
        startedOn: subscription.joined_at
          ? this.formatDateTime(subscription.joined_at)
          : "",
        currency: subscription.currency_cde || "",
        nextAmount: nextAmount != null ? this.formatNumber(nextAmount) : "",
        nextBillingDate: nextBillingDate
          ? this.formatDateTime(nextBillingDate)
          : "",
        status: subscription.status || apiData.tenantSubscriptionStatus || ""
      };

      this.potentialUsers = apiData.potentialUsers ?? null;

      const metrics = [];

      const activeUsers = Number(apiData.tenantActiveUsersCount) || 0;
      const totalUsers =
        Number(apiData.tenantTotalUsersCount) || activeUsers || 0;
      const usersAddOn = (subscription.add_ons || []).find(
        addOn => addOn.id === "users"
      );
      const paidUsers = Number(usersAddOn?.quantity) || 0;
      metrics.push({
        name: "Users",
        value: activeUsers,
        total: Math.max(paidUsers || totalUsers, 1),
        color: "#10B981",
        trail: "#E5E7EB",
        secondary: `Active / ${paidUsers} Paid`
      });

      const emailInbox = this.getFeatureUsage(
        apiData.emailUsage,
        "email-inbox"
      );
      if (emailInbox) {
        metrics.push(
          this.buildUsageMetric({
            name: "Email Inbox",
            usage: emailInbox,
            color: "#60A5FA"
          })
        );
      }

      const storageUsage = this.getFeatureUsage(
        apiData.storageUsage,
        "storage"
      );
      if (storageUsage) {
        const storageValue = this.convertBytesToGB(storageUsage.value);
        metrics.push({
          name: "Storage",
          value: storageValue.value,
          total: storageValue.total,
          color: "#F59E0B",
          trail: "#E5E7EB",
          secondary: `/${storageValue.label} Used`
        });
      }

      const emailOutbox = this.getFeatureUsage(
        apiData.emailUsage,
        "email-outbox"
      );
      if (emailOutbox) {
        metrics.push(
          this.buildUsageMetric({
            name: "Email Outbox",
            usage: emailOutbox,
            color: "#3B82F6"
          })
        );
      }

      this.metrics = metrics;

      this.subscriptionDetail = {
        planName: subscription.plan_name || "",
        status: subscription.status || apiData.tenantSubscriptionStatus || "",
        subscribedOn: subscription.joined_at || "",
        billingCycle: subscription.payment_currency || "",
        trialEndsAt: subscription.trial_ends_at || "",
        nextBillingDate: subscription.next_billing_at || "",
        nextBillingAmount: subscription.next_billing_amount
          ? `${subscription.payment_currency || ""} ${
              subscription.next_billing_amount
            }`
          : "",
        createdBy: ""
      };

      this.subscriptionLogs = (apiData.subscriptionActivityLogs || []).map(
        log => {
          const updatedBy = log.updated_by || {};
          const info = log.subscription_info || {};
          const name = updatedBy.name || updatedBy.email || "Unknown";
          const initials = name.charAt(0).toUpperCase();
          return {
            date: log.created_at ? this.formatDateTime(log.created_at) : "-",
            description:
              `The subscription plan was changed from` +
              ` (${info.old_plan_code || "-"})` +
              ` to (${info.new_plan_code || "-"})` +
              ` by (${name})`,
            avatarInitials: initials
          };
        }
      );
    },
    statusDotClass(status) {
      const normalized = (status || "")
        .toString()
        .trim()
        .toLowerCase();
      return [
        "h-2 w-2 rounded-full inline-block",
        normalized === "active" ? "bg-green-700" : "bg-red-700"
      ];
    },
    buildUsageMetric({ name, usage, color }) {
      const used = Number(usage.total_used ?? usage.value) || 0;
      const totalRaw = usage.total_available ?? usage.limit;
      const total = Math.max(Number(totalRaw) || used || 0, 1);
      const secondary = `/ ${this.formatNumber(total)}`;

      return {
        name,
        value: used,
        total,
        color,
        trail: "#E5E7EB",
        secondary
      };
    },
    getFeatureUsage(payload, featureName) {
      const normalizeKey = key => {
        if (typeof key !== "string") {
          return null;
        }
        const parts = key.split(/[-_]/).filter(Boolean);
        if (parts.length === 0) {
          return key;
        }
        return parts[parts.length - 1];
      };

      const keyCandidates = [featureName];
      const normalized = normalizeKey(featureName);
      if (normalized && normalized !== featureName) {
        keyCandidates.push(normalized);
      }

      if (Array.isArray(payload)) {
        return (
          payload.find(item => keyCandidates.includes(item.feature_name)) ||
          null
        );
      }

      if (payload && typeof payload === "object") {
        for (const key of keyCandidates) {
          const directKey = payload[key];
          if (directKey && typeof directKey === "object") {
            return {
              ...directKey,
              value: directKey.total_used ?? directKey.value ?? 0,
              limit: directKey.total_available ?? directKey.limit ?? 0,
              valid_upto: directKey.details?.valid_upto ?? directKey.valid_upto,
              total_used: directKey.total_used,
              total_available: directKey.total_available
            };
          }
        }

        const details = payload.details;
        if (Array.isArray(details)) {
          const match = details.find(item =>
            keyCandidates.includes(item.feature_name)
          );
          if (match) {
            return {
              ...match,
              value: match.total_used ?? match.value ?? 0,
              limit: match.total_available ?? match.limit ?? 0
            };
          }
        }
      }

      return null;
    },
    convertBytesToGB(bytes) {
      const numericBytes = Number(bytes) || 0;
      const gbValue = numericBytes / (1024 * 1024 * 1024);
      const rounded = Number(gbValue.toFixed(2));

      return {
        value: rounded,
        total: Math.max(rounded, 1),
        label: `${rounded} GB`
      };
    },
    normalizeLogoUrl(logo) {
      if (typeof logo !== "string") {
        return null;
      }

      const trimmedLogo = logo.trim();
      const allowedPattern = /^https:\/\/agentcisapp\.s3\.ap-southeast-2\.amazonaws\.com\/.+\.(png|jpe?g|gif|svg|webp)(\?.*)?$/i;

      return allowedPattern.test(trimmedLogo) ? trimmedLogo : null;
    },
    normalizeStatus(status) {
      if (!status) {
        return "";
      }

      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },
    normalizeWebsite(website) {
      if (!website) {
        return null;
      }

      if (/^https?:\/\//i.test(website)) {
        return website;
      }

      return `https://${website}`;
    },
    formatDateTime(dateString) {
      if (!dateString) {
        return "";
      }

      const formattedString = dateString.replace(" ", "T");
      const date = new Date(formattedString);

      if (Number.isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleString();
    },
    formatDate(dateString) {
      if (!dateString) {
        return "";
      }

      const formattedString = dateString.replace(" ", "T");
      const date = new Date(formattedString);

      if (Number.isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString();
    },
    formatNumber(value) {
      try {
        return new Intl.NumberFormat().format(Number(value));
      } catch (error) {
        return String(value);
      }
    },
    openMetricDrawer(metric) {
      if (!metric) {
        return;
      }
      this.selectedMetric = metric;
      this.isMetricDrawerOpen = true;
    },

    closeMetricDrawer() {
      this.isMetricDrawerOpen = false;
      setTimeout(() => {
        this.selectedMetric = null;
      }, 200);
    },

    openSubscriptionDrawer() {
      this.isSubscriptionDrawerOpen = true;
    },

    closeSubscriptionDrawer() {
      this.isSubscriptionDrawerOpen = false;
      setTimeout(() => {
        /* placeholder for possible future cleanup */
      }, 200);
    }
  },
  async created() {
    await this.$store.dispatch(
      "TenantAnalyticsStore/fetchUsers",
      this.$route.params.id
    );
    await this.fetchTenantData();
  }
};
</script>
<style scoped>
.drawer-width {
  width: 960px;
}
</style>
